import { useState, useRef, useEffect } from 'react'
import {
  Globe,
  Copy,
  ExternalLink,
  ImageIcon,
  FileText,
  Mail,
  MessageSquarePlus,
  ShoppingBag,
  Trash2,
  Plus,
  ChevronDown,
  ChevronUp,
  Upload,
  Save,
  CalendarCheck,
} from 'lucide-react'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { toast } from 'sonner'
import { useMyRestaurant, useUpdateRestaurant } from '@/lib/queries/useRestaurants'
import {
  useBookingPageConfig,
  useUpsertBookingPageConfig,
  uploadImage,
  type CustomQuestion,
} from '@/lib/queries/useBookingPageConfig'
import { useAppStore } from '@/lib/store/useAppStore'
import { STRAPI_BASE_URL } from '@/config/api'

// ─── Sub-components ────────────────────────────────────────────────────────────

function Toggle({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean
  onChange: () => void
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onChange}
      disabled={disabled}
      className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none ${
        disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'
      } ${checked && !disabled ? 'bg-[var(--color-accent-amber)]' : 'bg-[var(--color-bg-elevated)]'}`}
    >
      <span
        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-4' : 'translate-x-0'
        }`}
      />
    </button>
  )
}

function SectionCard({
  icon,
  iconClass,
  title,
  description,
  children,
  className,
}: {
  icon: React.ReactNode
  iconClass: string
  title: string
  description: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <Card className={`p-6 ${className ?? ''}`}>
      <div className="flex items-start gap-4 mb-5">
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-lg)] ${iconClass}`}
        >
          {icon}
        </div>
        <div>
          <h3 className="font-semibold text-[var(--color-text-primary)]">{title}</h3>
          <p className="text-sm text-[var(--color-text-secondary)] mt-0.5">{description}</p>
        </div>
      </div>
      {children}
    </Card>
  )
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toSlug(name: string) {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function CustomerBookingPage() {
  const { data: restaurantResp } = useMyRestaurant()
  const restaurant = Array.isArray((restaurantResp as any)?.data)
    ? (restaurantResp as any).data[0]
    : null
  const restaurantDocumentId = useAppStore((s) => s.restaurantId)

  const { data: configResp } = useBookingPageConfig()
  const existingConfig = Array.isArray((configResp as any)?.data)
    ? (configResp as any).data[0]
    : null

  const upsertConfig = useUpsertBookingPageConfig()
  const updateRestaurant = useUpdateRestaurant()

  // ── Slug auto-save ────────────────────────────────────────────────────────
  const savedSlug = restaurant?.slug ?? ''
  const derivedSlug = !savedSlug && restaurant?.name ? toSlug(restaurant.name) : ''
  const [slug, setSlug] = useState('')
  const slugAutoSaved = useRef(false)

  useEffect(() => {
    if (savedSlug) {
      setSlug(savedSlug)
    } else if (derivedSlug && restaurantDocumentId && !slugAutoSaved.current) {
      slugAutoSaved.current = true
      setSlug(derivedSlug)
      updateRestaurant.mutate({ documentId: restaurantDocumentId, slug: derivedSlug } as any)
    }
  }, [savedSlug, derivedSlug, restaurantDocumentId])

  // ── Form state ────────────────────────────────────────────────────────────
  const [enabled, setEnabled] = useState(true)
  const [coverImageFile, setCoverImageFile] = useState<File | null>(null)
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(null)
  const [existingCoverImageId, setExistingCoverImageId] = useState<number | null>(null)
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [existingLogoId, setExistingLogoId] = useState<number | null>(null)
  const [terms, setTerms] = useState('')
  const [requireTerms, setRequireTerms] = useState(true)
  const [allowReservations, setAllowReservations] = useState(true)
  const [showEmail, setShowEmail] = useState(true)
  const [requireEmail, setRequireEmail] = useState(false)
  const [allowPreOrder, setAllowPreOrder] = useState(false)
  const [allowCollectionOrders, setAllowCollectionOrders] = useState(false)
  const [allowScheduledCollectionOrders, setAllowScheduledCollectionOrders] = useState(true)
  const [scheduledOrderLeadMinutes, setScheduledOrderLeadMinutes] = useState('30')
  const [deliveryOrderSlotIntervalMinutes, setDeliveryOrderSlotIntervalMinutes] = useState('0')
  const [deliveryOrderSlotCapacity, setDeliveryOrderSlotCapacity] = useState('0')
  const [questions, setQuestions] = useState<CustomQuestion[]>([])
  const [newQLabel, setNewQLabel] = useState('')
  const [newQType, setNewQType] = useState<'text' | 'select'>('text')
  const [newQRequired, setNewQRequired] = useState(false)
  const [newQOptions, setNewQOptions] = useState('')
  const [expandedQ, setExpandedQ] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'general' | 'workflows' | 'form'>('general')

  const fileInputRef = useRef<HTMLInputElement>(null)
  const logoInputRef = useRef<HTMLInputElement>(null)
  const initialLoadApplied = useRef(false)

  // Pre-fill form from saved config (once)
  useEffect(() => {
    if (!existingConfig || initialLoadApplied.current) return
    initialLoadApplied.current = true
    setEnabled(existingConfig.enabled ?? true)
    setTerms(existingConfig.termsAndConditions ?? '')
    setRequireTerms(existingConfig.requireTerms ?? true)
    setAllowReservations(existingConfig.allowReservations ?? true)
    setShowEmail(existingConfig.showEmail ?? true)
    setRequireEmail(existingConfig.requireEmail ?? false)
    setAllowPreOrder(existingConfig.allowPreOrder ?? false)
    setAllowCollectionOrders(existingConfig.allowCollectionOrders ?? false)
    setAllowScheduledCollectionOrders(existingConfig.allowScheduledCollectionOrders ?? true)
    setScheduledOrderLeadMinutes(String(existingConfig.scheduledOrderLeadMinutes ?? 30))
    setDeliveryOrderSlotIntervalMinutes(String(existingConfig.deliveryOrderSlotIntervalMinutes ?? 0))
    setDeliveryOrderSlotCapacity(String(existingConfig.deliveryOrderSlotCapacity ?? 0))
    setQuestions(existingConfig.customQuestions ?? [])
    if (existingConfig.coverImage?.id) {
      setExistingCoverImageId(existingConfig.coverImage.id)
      const url: string = existingConfig.coverImage.url
      setCoverImagePreview(url.startsWith('http') ? url : `${STRAPI_BASE_URL}${url}`)
    }
    if (existingConfig.logo?.id) {
      setExistingLogoId(existingConfig.logo.id)
      const url: string = existingConfig.logo.url
      setLogoPreview(url.startsWith('http') ? url : `${STRAPI_BASE_URL}${url}`)
    }
  }, [existingConfig])

  const publicUrl = slug ? `https://sheanswers.com/restaurants/${slug}` : ''

  // ── Cover image ───────────────────────────────────────────────────────────
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setCoverImageFile(file)
    const reader = new FileReader()
    reader.onload = (ev) => setCoverImagePreview(ev.target?.result as string)
    reader.readAsDataURL(file)
  }

  // ── Logo (PNG only) ───────────────────────────────────────────────────────
  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.type !== 'image/png') {
      toast.error('Logo must be a PNG file')
      e.target.value = ''
      return
    }
    setLogoFile(file)
    const reader = new FileReader()
    reader.onload = (ev) => setLogoPreview(ev.target?.result as string)
    reader.readAsDataURL(file)
  }

  // ── Custom questions ──────────────────────────────────────────────────────
  const addQuestion = () => {
    if (!newQLabel.trim()) return
    const q: CustomQuestion = {
      id: `q-${Date.now()}`,
      label: newQLabel.trim(),
      type: newQType,
      required: newQRequired,
      options:
        newQType === 'select'
          ? newQOptions.split(',').map((s) => s.trim()).filter(Boolean)
          : undefined,
    }
    setQuestions((prev) => [...prev, q])
    setNewQLabel('')
    setNewQOptions('')
    setNewQRequired(false)
    setNewQType('text')
  }

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    try {
      const deliveryCapacity = Math.min(100, Math.max(0, Number.parseInt(deliveryOrderSlotCapacity || '0', 10) || 0))
      const deliveryInterval = Math.min(180, Math.max(0, Number.parseInt(deliveryOrderSlotIntervalMinutes || '0', 10) || 0))
      if (deliveryCapacity > 0 && deliveryInterval === 0) {
        toast.error('Set a slot interval when an order capacity limit is enabled')
        return
      }

      let coverImageId: number | null | undefined = undefined
      if (coverImageFile) {
        coverImageId = await uploadImage(coverImageFile)
        setCoverImageFile(null) // clear so re-save doesn't re-upload
      } else if (existingCoverImageId) {
        coverImageId = existingCoverImageId
      }

      let logoId: number | null | undefined = undefined
      if (logoFile) {
        logoId = await uploadImage(logoFile)
        setLogoFile(null)
      } else if (existingLogoId) {
        logoId = existingLogoId
      } else {
        logoId = null
      }

      await upsertConfig.mutateAsync({
        enabled,
        coverImageId,
        logoId,
        termsAndConditions: terms,
        requireTerms,
        allowReservations,
        showEmail,
        requireEmail,
        allowPreOrder,
        allowCollectionOrders,
        allowScheduledCollectionOrders,
        scheduledOrderLeadMinutes: Math.min(
          180,
          Math.max(0, Number.parseInt(scheduledOrderLeadMinutes || '30', 10) || 30),
        ),
        deliveryOrderSlotIntervalMinutes: deliveryCapacity > 0 ? deliveryInterval : 0,
        deliveryOrderSlotCapacity: deliveryCapacity,
        customQuestions: questions,
      })

      if (restaurantDocumentId) {
        updateRestaurant.mutate({
          documentId: restaurantDocumentId,
          featureCustomerPage: enabled,
        } as any)
      }
    } catch {
      // errors handled in mutations
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────
  const tabs = [
    { id: 'general' as const, label: 'General', icon: Globe },
    { id: 'workflows' as const, label: 'Workflows', icon: ShoppingBag },
    { id: 'form' as const, label: 'Form', icon: FileText },
  ]

  return (
    <div>
      <PageHeader
        title="Customer Page"
        description="Public page where guests can reserve tables and place collection orders"
      />

      {/* URL Banner */}
      {slug && (
        <div className="flex w-full max-w-6xl items-center gap-2 rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] p-3 mb-6">
          <Globe className="h-4 w-4 text-[var(--color-text-muted)] shrink-0" />
          <span className="flex-1 text-sm font-mono text-[var(--color-text-primary)] truncate">
            {publicUrl}
          </span>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => {
              navigator.clipboard.writeText(publicUrl)
              toast.success('Link copied')
            }}
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="icon-sm" onClick={() => window.open(publicUrl, '_blank')}>
            <ExternalLink className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}

      <div className="w-full max-w-6xl space-y-5">
        <div className="flex gap-1 overflow-x-auto rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-bg-subtle)] p-1">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setActiveTab(id)}
              className={`flex min-w-max items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                activeTab === id
                  ? 'bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)] shadow-sm'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>

        {activeTab === 'general' && (
          <div className="grid gap-5 xl:grid-cols-2">

        {/* ── Page Visibility ── */}
        <SectionCard
          icon={<Globe className="h-5 w-5 text-blue-400" />}
          iconClass="bg-blue-500/10"
          title="Page visibility"
          description="Enable or disable your public customer page"
          className="xl:col-span-2"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[var(--color-text-primary)]">
                Enable public customer page
              </p>
              <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
                Guests can discover and book or order at your restaurant
              </p>
            </div>
            <Toggle checked={enabled} onChange={() => setEnabled((v) => !v)} />
          </div>
        </SectionCard>

        {/* ── Logo ── */}
        <SectionCard
          icon={<ImageIcon className="h-5 w-5 text-pink-400" />}
          iconClass="bg-pink-500/10"
          title="Logo"
          description="Shown centered on the left side of your booking page. PNG only."
        >
          <input
            ref={logoInputRef}
            type="file"
            accept="image/png"
            className="hidden"
            onChange={handleLogoChange}
          />
          {logoPreview ? (
            <div className="relative rounded-[var(--radius-lg)] overflow-hidden border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)]">
              <div className="flex items-center justify-center h-40">
                <img src={logoPreview} alt="Logo" className="max-h-full max-w-[60%] object-contain" />
              </div>
              <div className="absolute bottom-3 right-3 flex gap-2">
                <Button variant="secondary" size="sm" onClick={() => logoInputRef.current?.click()}>
                  <Upload className="h-3.5 w-3.5 mr-1" /> Change
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setLogoPreview(null)
                    setLogoFile(null)
                    setExistingLogoId(null)
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                </Button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => logoInputRef.current?.click()}
              className="w-full flex flex-col items-center justify-center gap-2 h-40 rounded-[var(--radius-lg)] border-2 border-dashed border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)] hover:border-[var(--color-accent-amber)]/50 hover:text-[var(--color-text-secondary)] transition-colors cursor-pointer"
            >
              <Upload className="h-5 w-5" />
              <span className="text-sm">Click to upload logo</span>
              <span className="text-xs">PNG only — transparent background recommended</span>
            </button>
          )}
        </SectionCard>

        {/* ── Cover Image ── */}
        <SectionCard
          icon={<ImageIcon className="h-5 w-5 text-purple-400" />}
          iconClass="bg-purple-500/10"
          title="Cover image"
          description="Shown on the right side of your booking page"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />
          {coverImagePreview ? (
            <div className="relative rounded-[var(--radius-lg)] overflow-hidden border border-[var(--color-border-default)]">
              <img src={coverImagePreview} alt="Cover" className="w-full h-52 object-cover" />
              <div className="absolute bottom-3 right-3 flex gap-2">
                <Button variant="secondary" size="sm" onClick={() => fileInputRef.current?.click()}>
                  <Upload className="h-3.5 w-3.5 mr-1" /> Change
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setCoverImagePreview(null)
                    setCoverImageFile(null)
                    setExistingCoverImageId(null)
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                </Button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full flex flex-col items-center justify-center gap-2 h-40 rounded-[var(--radius-lg)] border-2 border-dashed border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)] hover:border-[var(--color-accent-amber)]/50 hover:text-[var(--color-text-secondary)] transition-colors cursor-pointer"
            >
              <Upload className="h-5 w-5" />
              <span className="text-sm">Click to upload image</span>
              <span className="text-xs">JPG, PNG, WebP — max 5 MB recommended</span>
            </button>
          )}
        </SectionCard>

          </div>
        )}

        {activeTab === 'workflows' && (
          <div className="grid gap-5 xl:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
        {/* ── Reservations ── */}
        <SectionCard
          icon={<CalendarCheck className="h-5 w-5 text-blue-400" />}
          iconClass="bg-blue-500/10"
          title="Reservations"
          description="Let guests book a table on this page"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[var(--color-text-primary)]">
                Allow reservations
              </p>
              <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
                Turn this off if your customer page is for collection / delivery only
              </p>
            </div>
            <Toggle checked={allowReservations} onChange={() => setAllowReservations((v) => !v)} />
          </div>
        </SectionCard>

        {/* ── Orders & Pre-orders ── */}
        <SectionCard
          icon={<ShoppingBag className="h-5 w-5 text-orange-400" />}
          iconClass="bg-orange-500/10"
          title="Orders & pre-orders"
          description="Browsable menu, collection and delivery options"
        >
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-[var(--color-text-primary)]">
                  Allow pre-orders
                </p>
                <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
                  Guests can browse the menu and pre-order while reserving a table
                </p>
              </div>
              <Toggle
                checked={allowPreOrder}
                onChange={() => setAllowPreOrder((v) => !v)}
                disabled={!allowReservations}
              />
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-[var(--color-border-subtle)]">
              <div>
                <p className="text-sm font-medium text-[var(--color-text-primary)]">
                  Allow collection orders
                </p>
                <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
                  Guests can order from the menu for collection or delivery, without booking a table
                </p>
              </div>
              <Toggle
                checked={allowCollectionOrders}
                onChange={() => setAllowCollectionOrders((v) => !v)}
              />
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-[var(--color-border-subtle)]">
              <div>
                <p className="text-sm font-medium text-[var(--color-text-primary)]">
                  Allow scheduled ordering
                </p>
                <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
                  Let guests pick a future collection or delivery time instead of ordering ASAP only
                </p>
              </div>
              <Toggle
                checked={allowCollectionOrders ? allowScheduledCollectionOrders : false}
                onChange={() => setAllowScheduledCollectionOrders((v) => !v)}
                disabled={!allowCollectionOrders}
              />
            </div>

            <div className="rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] px-3 py-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-[var(--color-text-primary)]">
                    Move scheduled orders to Now
                  </p>
                  <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
                    Ring the device and move accepted scheduled orders into the live Now queue before pickup time
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    max={180}
                    step={1}
                    value={scheduledOrderLeadMinutes}
                    onChange={(e) => setScheduledOrderLeadMinutes(e.target.value)}
                    disabled={!allowCollectionOrders || !allowScheduledCollectionOrders}
                    className="h-9 w-20 rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-base)] px-3 text-sm text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-accent-amber)]/50 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <span className="text-xs font-medium text-[var(--color-text-secondary)]">
                    min
                  </span>
                </div>
              </div>
            </div>

            <div className="rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] px-3 py-3 space-y-3">
              <div>
                <p className="text-sm font-medium text-[var(--color-text-primary)]">Order capacity per fulfillment type</p>
                <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
                  This setting applies separately to delivery and collection. For example, a limit of 1 allows one delivery and one collection order in each time slot. 0 means unlimited.
                </p>
              </div>
              <div className="grid max-w-xl grid-cols-2 gap-2">
                <label className="text-xs text-[var(--color-text-secondary)]">
                  Slot interval (min)
                  <input
                    type="number"
                    min={1}
                    max={180}
                    step={1}
                    value={deliveryOrderSlotIntervalMinutes}
                    onChange={(e) => setDeliveryOrderSlotIntervalMinutes(e.target.value)}
                    disabled={Number(deliveryOrderSlotCapacity) <= 0}
                    className="mt-1 h-9 w-full rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-base)] px-3 text-sm text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-accent-amber)]/50 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </label>
                <label className="text-xs text-[var(--color-text-secondary)]">
                  Max orders per slot, for each type
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    value={deliveryOrderSlotCapacity}
                    onChange={(e) => setDeliveryOrderSlotCapacity(e.target.value)}
                    className="mt-1 h-9 w-full rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-base)] px-3 text-sm text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-accent-amber)]/50"
                  />
                </label>
              </div>
            </div>
          </div>
        </SectionCard>

          </div>
        )}

        {activeTab === 'form' && (
          <div className="grid gap-5 xl:grid-cols-2">
        {/* ── Contact Fields ── */}
        <SectionCard
          icon={<Mail className="h-5 w-5 text-[var(--color-accent-amber)]" />}
          iconClass="bg-[var(--color-accent-amber-dim)]"
          title="Contact fields"
          description="Name and phone are always required"
        >
          <div className="space-y-2">
            {(['Name', 'Phone'] as const).map((label) => (
              <div
                key={label}
                className="flex items-center justify-between px-3 py-2.5 rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] opacity-50 pointer-events-none select-none"
              >
                <div>
                  <p className="text-sm font-medium text-[var(--color-text-primary)]">{label}</p>
                  <p className="text-xs text-[var(--color-text-muted)]">Always shown, always required</p>
                </div>
                <span className="text-xs font-medium text-[var(--color-accent-amber)]">Required</span>
              </div>
            ))}

            <div className="px-3 py-2.5 rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] border border-[var(--color-border-default)]">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-[var(--color-text-primary)]">Email</p>
                  <p className="text-xs text-[var(--color-text-muted)]">Show email field to guests</p>
                </div>
                <Toggle
                  checked={showEmail}
                  onChange={() => {
                    setShowEmail((v) => !v)
                    if (showEmail) setRequireEmail(false)
                  }}
                />
              </div>
              {showEmail && (
                <div className="flex items-center justify-between pt-2.5 mt-2.5 border-t border-[var(--color-border-subtle)]">
                  <p className="text-xs text-[var(--color-text-secondary)]">Make email required</p>
                  <Toggle checked={requireEmail} onChange={() => setRequireEmail((v) => !v)} />
                </div>
              )}
            </div>

            <div className="flex items-center justify-between px-3 py-2.5 rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] opacity-50 pointer-events-none select-none">
              <div>
                <p className="text-sm font-medium text-[var(--color-text-primary)]">Message</p>
                <p className="text-xs text-[var(--color-text-muted)]">Always shown, optional</p>
              </div>
              <span className="text-xs text-[var(--color-text-muted)]">Optional</span>
            </div>
          </div>
        </SectionCard>

        {/* ── Terms & Conditions ── */}
        <SectionCard
          icon={<FileText className="h-5 w-5 text-[var(--color-accent-green)]" />}
          iconClass="bg-[var(--color-accent-green-dim)]"
          title="Terms & Conditions"
          description="Show terms text before guests submit. Leave blank to skip entirely."
        >
          <textarea
            value={terms}
            onChange={(e) => setTerms(e.target.value)}
            placeholder={`Enter your booking terms and conditions here…\n\ne.g. Cancellations must be made at least 24 hours in advance. No-shows may be charged a fee.`}
            rows={7}
            className="w-full px-3 py-2.5 rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] border border-[var(--color-border-default)] focus:outline-none focus:border-[var(--color-accent-amber)]/50 resize-y"
          />
          {terms.trim() && (
            <div className="flex items-center justify-between pt-3 mt-3 border-t border-[var(--color-border-subtle)]">
              <div>
                <p className="text-sm font-medium text-[var(--color-text-primary)]">
                  Require guests to accept
                </p>
                <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
                  When off, the terms are shown but the checkbox is optional
                </p>
              </div>
              <Toggle checked={requireTerms} onChange={() => setRequireTerms((v) => !v)} />
            </div>
          )}
        </SectionCard>

        {/* ── Custom Questions ── */}
        <SectionCard
          icon={<MessageSquarePlus className="h-5 w-5 text-[var(--color-accent-amber)]" />}
          iconClass="bg-[var(--color-accent-amber-dim)]"
          title="Custom questions"
          description="Extra questions shown to guests during checkout"
          className="xl:col-span-2"
        >
          {questions.length > 0 && (
            <div className="space-y-2 mb-4">
              {questions.map((q) => (
                <div
                  key={q.id}
                  className="rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] overflow-hidden"
                >
                  <div
                    className="flex items-center gap-3 px-3 py-2.5 cursor-pointer"
                    onClick={() => setExpandedQ((prev) => (prev === q.id ? null : q.id))}
                  >
                    <span className="flex-1 text-sm font-medium text-[var(--color-text-primary)]">
                      {q.label}
                    </span>
                    <span className="text-xs text-[var(--color-text-muted)]">
                      {q.type} · {q.required ? 'required' : 'optional'}
                    </span>
                    {expandedQ === q.id ? (
                      <ChevronUp className="h-3.5 w-3.5 text-[var(--color-text-muted)]" />
                    ) : (
                      <ChevronDown className="h-3.5 w-3.5 text-[var(--color-text-muted)]" />
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setQuestions((prev) => prev.filter((x) => x.id !== q.id))
                      }}
                      className="p-1 rounded text-[var(--color-text-muted)] hover:text-red-400 transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  {expandedQ === q.id && q.type === 'select' && (
                    <div className="px-3 pb-2.5 text-xs text-[var(--color-text-secondary)]">
                      Options: {(q.options ?? []).join(', ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] p-3 space-y-2">
            <p className="text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide">
              Add question
            </p>
            <input
              placeholder="Question (e.g. Do you have allergies?)"
              value={newQLabel}
              onChange={(e) => setNewQLabel(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addQuestion())}
              className="w-full h-9 px-3 rounded-[var(--radius-md)] bg-[var(--color-bg-base)] text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] border border-[var(--color-border-default)] focus:outline-none focus:border-[var(--color-accent-amber)]/50"
            />
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={newQType}
                onChange={(e) => setNewQType(e.target.value as 'text' | 'select')}
                className="h-8 px-2 rounded-[var(--radius-md)] bg-[var(--color-bg-base)] text-sm text-[var(--color-text-primary)] border border-[var(--color-border-default)] focus:outline-none"
              >
                <option value="text">Text</option>
                <option value="select">Dropdown</option>
              </select>
              <label className="flex items-center gap-1.5 text-xs text-[var(--color-text-secondary)] cursor-pointer">
                <input
                  type="checkbox"
                  checked={newQRequired}
                  onChange={() => setNewQRequired((v) => !v)}
                  className="rounded"
                />
                Required
              </label>
              <Button
                variant="secondary"
                size="sm"
                onClick={addQuestion}
                type="button"
                className="ml-auto h-8"
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> Add
              </Button>
            </div>
            {newQType === 'select' && (
              <input
                placeholder="Options, comma-separated (e.g. Birthday, Anniversary)"
                value={newQOptions}
                onChange={(e) => setNewQOptions(e.target.value)}
                className="w-full h-9 px-3 rounded-[var(--radius-md)] bg-[var(--color-bg-base)] text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] border border-[var(--color-border-default)] focus:outline-none focus:border-[var(--color-accent-amber)]/50"
              />
            )}
          </div>
        </SectionCard>

          </div>
        )}

        {/* ── Save ── */}
        <div className="flex justify-end pb-8">
          <Button onClick={handleSave} loading={upsertConfig.isPending} size="lg">
            <Save className="h-4 w-4 mr-1.5" /> Save settings
          </Button>
        </div>
      </div>
    </div>
  )
}
